import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { ReturnRequest } from '../../types';
import { useToast } from '../../contexts/ToastContext';
import SimpleFormModal from '../../components/SimpleFormModal';
import './AdminReturns.css';

const AdminReturns: React.FC = () => {
  const [returns, setReturns] = useState<ReturnRequest[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [selectedReturn, setSelectedReturn] = useState<ReturnRequest | null>(null);
  const [processing, setProcessing] = useState<{ id: number; action: string } | null>(null);
  const [rejectingId, setRejectingId] = useState<number | null>(null);
  const { success, error: showError, warning } = useToast();

  const fetchReturns = useCallback(async (): Promise<void> => {
    try {
      const params = statusFilter ? { status: statusFilter } : {};
      const response = await api.get<ReturnRequest[]>('/admin/returns', { params });
      setReturns(response.data);
    } catch (error) {
      console.error('Failed to fetch returns:', error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchReturns();
  }, [fetchReturns]);

  const updateReturnStatus = async (returnId: number, status: string): Promise<void> => {
    setProcessing({ id: returnId, action: status });
    try {
      await api.put(`/admin/returns/${returnId}/status`, { status });
      fetchReturns();
      setSelectedReturn(null);
      success('Return status updated.');
    } catch (error: any) {
      showError('Failed to update return status: ' + (error.response?.data?.message || error.message));
    } finally {
      setProcessing(null);
    }
  };

  const approveReturn = async (returnId: number): Promise<void> => {
    setProcessing({ id: returnId, action: 'approve' });
    try {
      await api.post(`/admin/returns/${returnId}/approve`);
      fetchReturns();
      setSelectedReturn(null);
      success('Return approved.');
    } catch (error: any) {
      showError('Failed to approve return: ' + (error.response?.data?.message || error.message));
    } finally {
      setProcessing(null);
    }
  };

  const rejectReturn = async (returnId: number, reason: string): Promise<void> => {
    if (!reason.trim()) {
      warning('Rejection reason is required.');
      return;
    }
    setProcessing({ id: returnId, action: 'reject' });
    try {
      await api.post(`/admin/returns/${returnId}/reject`, { reason });
      fetchReturns();
      setSelectedReturn(null);
      success('Return rejected.');
      setRejectingId(null);
    } catch (error: any) {
      showError('Failed to reject return: ' + (error.response?.data?.message || error.message));
    } finally {
      setProcessing(null);
    }
  };

  const getStatusBadgeClass = (status: string): string => {
    const statusMap: Record<string, string> = {
      'Pending': 'badge-warning',
      'Approved': 'badge-info',
      'Rejected': 'badge-danger',
      'Received': 'badge-primary',
      'Refunded': 'badge-success',
      'Cancelled': 'badge-secondary'
    };
    return statusMap[status] || 'badge-secondary';
  };

  if (loading) return <div className="admin-loading">Loading returns...</div>;

  return (
    <div className="admin-returns">
      <div className="admin-header">
        <h1>Return Management</h1>
        <div className="filters">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">All Statuses</option>
            <option value="Pending">Pending</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
            <option value="Received">Received</option>
            <option value="Refunded">Refunded</option>
            <option value="Cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      <div className="returns-container">
        <div className="returns-list">
          <table className="admin-table">
            <thead>
              <tr>
                <th>RMA Number</th>
                <th>Order</th>
                <th>Product</th>
                <th>Quantity</th>
                <th>Reason</th>
                <th>Refund Amount</th>
                <th>Status</th>
                <th>Requested</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {returns.length === 0 ? (
                <tr>
                  <td colSpan={9}>No returns found</td>
                </tr>
              ) : (
                returns.map((returnItem) => (
                  <tr
                    key={returnItem.id}
                    className={selectedReturn?.id === returnItem.id ? 'selected' : ''}
                    onClick={() => setSelectedReturn(returnItem)}
                  >
                    <td><strong>{returnItem.rmaNumber || `RMA-${returnItem.id}`}</strong></td>
                    <td>#{returnItem.order?.orderNumber || returnItem.orderId}</td>
                    <td>{returnItem.orderItem?.product?.name || 'N/A'}</td>
                    <td>{returnItem.quantity}</td>
                    <td>{returnItem.reason}</td>
                    <td>${returnItem.refundAmount?.toFixed(2) || '0.00'}</td>
                    <td>
                      <span className={`badge ${getStatusBadgeClass(returnItem.status)}`}>
                        {returnItem.status}
                      </span>
                    </td>
                    <td>{new Date(returnItem.requestDate || returnItem.requestedDate || '').toLocaleDateString()}</td>
                    <td>
                      {returnItem.status === 'Pending' && (
                        <>
                          <button
                            className="btn btn-sm btn-success"
                            onClick={(e) => {
                              e.stopPropagation();
                              approveReturn(returnItem.id);
                            }}
                            disabled={processing?.id === returnItem.id}
                          >
                            {processing?.id === returnItem.id && <span className="btn-spinner" aria-hidden="true"></span>}
                            Approve
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={(e) => {
                              e.stopPropagation();
                              setRejectingId(returnItem.id);
                            }}
                            disabled={processing?.id === returnItem.id}
                          >
                            {processing?.id === returnItem.id && <span className="btn-spinner" aria-hidden="true"></span>}
                            Reject
                          </button>
                        </>
                      )}
                      {returnItem.status === 'Approved' && (
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            updateReturnStatus(returnItem.id, 'Received');
                          }}
                          disabled={processing?.id === returnItem.id}
                        >
                          {processing?.id === returnItem.id && <span className="btn-spinner" aria-hidden="true"></span>}
                          Mark Received
                        </button>
                      )}
                      {returnItem.status === 'Received' && (
                        <button
                          className="btn btn-sm btn-success"
                          onClick={(e) => {
                            e.stopPropagation();
                            updateReturnStatus(returnItem.id, 'Refunded');
                          }}
                          disabled={processing?.id === returnItem.id}
                        >
                          {processing?.id === returnItem.id && <span className="btn-spinner" aria-hidden="true"></span>}
                          Process Refund
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {selectedReturn && (
          <div className="return-details">
            <h2>Return Details</h2>
            <div className="detail-section">
              <h3>Return Information</h3>
              <p><strong>RMA Number:</strong> {selectedReturn.rmaNumber || `RMA-${selectedReturn.id}`}</p>
              <p><strong>Status:</strong> <span className={`badge ${getStatusBadgeClass(selectedReturn.status)}`}>{selectedReturn.status}</span></p>
              <p><strong>Reason:</strong> {selectedReturn.reason}</p>
              <p><strong>Quantity:</strong> {selectedReturn.quantity}</p>
              <p><strong>Refund Amount:</strong> ${selectedReturn.refundAmount?.toFixed(2) || '0.00'}</p>
              {selectedReturn.description && (
                <p><strong>Description:</strong> {selectedReturn.description}</p>
              )}
            </div>

            <div className="detail-section">
              <h3>Order Information</h3>
              <p><strong>Order Number:</strong> {selectedReturn.order?.orderNumber || selectedReturn.orderId}</p>
              <p><strong>Order Date:</strong> {selectedReturn.order?.createdAt ? new Date(selectedReturn.order.createdAt).toLocaleDateString() : 'N/A'}</p>
            </div>

            <div className="detail-section">
              <h3>Product Information</h3>
              <p><strong>Product:</strong> {selectedReturn.orderItem?.product?.name || 'N/A'}</p>
              <p><strong>Unit Price:</strong> ${selectedReturn.orderItem?.unitPrice?.toFixed(2) || '0.00'}</p>
            </div>

            {selectedReturn.adminNotes && (
              <div className="detail-section">
                <h3>Admin Notes</h3>
                <p>{selectedReturn.adminNotes}</p>
              </div>
            )}

            <div className="detail-section">
              <h3>Timeline</h3>
              <p><strong>Requested:</strong> {new Date(selectedReturn.requestDate || selectedReturn.requestedDate || '').toLocaleString()}</p>
              {selectedReturn.approvedDate && (
                <p><strong>Approved:</strong> {new Date(selectedReturn.approvedDate).toLocaleString()}</p>
              )}
              {selectedReturn.receivedDate && (
                <p><strong>Received:</strong> {new Date(selectedReturn.receivedDate).toLocaleString()}</p>
              )}
              {selectedReturn.refundedDate && (
                <p><strong>Refunded:</strong> {new Date(selectedReturn.refundedDate).toLocaleString()}</p>
              )}
              {selectedReturn.refundTransactionId && (
                <p><strong>Refund Transaction ID:</strong> {selectedReturn.refundTransactionId}</p>
              )}
            </div>

            <button className="btn btn-secondary" onClick={() => setSelectedReturn(null)}>
              Close
            </button>
          </div>
        )}
      </div>
      <SimpleFormModal
        isOpen={rejectingId !== null}
        title="Reject Return"
        fields={[
          { name: 'reason', label: 'Rejection Reason', type: 'textarea', required: true, placeholder: 'Add rejection reason...' }
        ]}
        submitLabel="Reject Return"
        loading={processing?.action === 'reject'}
        onSubmit={(values) => {
          if (rejectingId) {
            rejectReturn(rejectingId, values.reason || '');
          }
        }}
        onClose={() => setRejectingId(null)}
      />
    </div>
  );
};

export default AdminReturns;
